import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_TIME_ZONES } from '../graphql/queries';
import { canonicalTimezone, findTimezoneOption, searchTimezones, timezoneOffset } from './timezoneOptions';

function timezoneRegion(zone) {
  const region = zone.region?.name || zone.timezone.split('/')[0];
  return ({America:'Americas',Etc:'Other',UTC:'Other',Indian:'Indian Ocean'})[region] || region;
}

export default function TimezoneSelector({ timezoneId, onTimezoneSelect, isDisabled = false }) {
  const { loading, error, data } = useQuery(GET_TIME_ZONES);
  const options = useMemo(() => (data?.getTimeZones || []).filter(zone => canonicalTimezone(zone.timezone)).sort((a,b) => timezoneRegion(a).localeCompare(timezoneRegion(b)) || a.name.localeCompare(b.name)), [data]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedRegions, setExpandedRegions] = useState({});
  const editing = useRef(false);
  const notified = useRef(false);
  const callback = useRef(onTimezoneSelect);
  callback.current = onTimezoneSelect;

  useEffect(() => {
    if (!options.length) return;
    const value = findTimezoneOption(options, timezoneId || Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (!value) return;
    setSelected(value);
    if (!editing.current) setSearch(value.name);
    if (!notified.current && callback.current) {
      notified.current = true;
      callback.current(value);
    }
  }, [options, timezoneId]);

  return <Autocomplete
    options={options}
    groupBy={timezoneRegion}
    renderGroup={params => {
      const searching = editing.current && search.trim().length > 0;
      const expanded = searching || !!expandedRegions[params.group];
      return <li key={params.key}>
        <Button fullWidth aria-expanded={expanded} onMouseDown={event=>event.preventDefault()} onClick={()=>setExpandedRegions(previous=>({...previous,[params.group]:!expanded}))} disabled={searching} sx={{justifyContent:'space-between',px:2,py:1,textTransform:'none',fontWeight:700,bgcolor:'action.hover','&.Mui-disabled':{color:'text.primary'}}}>
          <span>{params.group}</span><span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        </Button>
        {expanded && <Box component="ul" sx={{p:0,m:0,listStyle:'none'}}>{params.children}</Box>}
      </li>;
    }}
    value={selected}
    inputValue={search}
    loading={loading}
    disabled={isDisabled || !!error}
    openOnFocus
    disableClearable
    getOptionLabel={zone => zone.name}
    isOptionEqualToValue={(a,b) => String(a.id) === String(b.id)}
    filterOptions={(items,{inputValue}) => searchTimezones(items,inputValue)}
    onInputChange={(_,value,reason) => {
      if (reason === 'input' || reason === 'clear' || !editing.current) setSearch(value);
    }}
    onFocus={() => { editing.current = true; setSearch(''); }}
    onBlur={() => { editing.current = false; setSearch(selected?.name || ''); }}
    onChange={(_,zone) => {
      if (!zone) return;
      editing.current = false;
      setSelected(zone);
      setSearch(zone.name);
      callback.current?.(zone);
    }}
    noOptionsText="No matching timezones. Try a nearby major city."
    renderInput={params => <TextField {...params} size="small" label="Timezone" placeholder="Search city or timezone" error={!!error} helperText={error ? 'Could not load timezones. Refresh to try again.' : undefined} />}
    renderOption={(props,zone) => <li {...props} key={zone.id}><Box>
      <Typography variant="body2">{zone.name}</Typography>
      <Typography variant="caption" color="text.secondary">{zone.timezone.replaceAll('_',' ')} · {timezoneOffset(zone.timezone)} · {new Intl.DateTimeFormat('en-US',{timeZone:zone.timezone,hour:'numeric',minute:'2-digit'}).format(new Date())}</Typography>
    </Box></li>}
  />;
}
